const PRIMARY_SEARCH_FIELDS = [
  'number',
  'crossReference',
  'name',
  'skillNo',
  'skill',
  'customer',
  'email',
  'givenName',
  'familyName',
]

const SKIP_NESTED_KEYS = new Set([
  '@context',
  '@id',
  '@type',
  'file',
  'image',
  'hydra:member',
])

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeForSearch(value) {
  return String(value).trim().toLowerCase()
}

function scoreFieldValue(value, term) {
  const normalized = normalizeForSearch(value)
  if (!normalized) {
    return null
  }

  if (normalized === term) {
    return 0
  }
  if (normalized.startsWith(term)) {
    return 1
  }
  try {
    if (new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i').test(normalized)) {
      return 2
    }
  } catch {
    // ignore invalid regex from search term
  }
  if (normalized.includes(term)) {
    return 3
  }

  return null
}

function getPrimaryFieldScore(row, term) {
  let best = null

  PRIMARY_SEARCH_FIELDS.forEach((field) => {
    if (row[field] == null || row[field] === '') {
      return
    }

    const score = scoreFieldValue(row[field], term)
    if (score !== null) {
      best = best === null ? score : Math.min(best, score)
    }
  })

  return best
}

function collectNestedStrings(value, strings, depth = 0) {
  if (value == null || depth > 5) {
    return
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed) {
      strings.push(trimmed)
    }
    return
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    strings.push(String(value))
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectNestedStrings(item, strings, depth + 1))
    return
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, nested]) => {
      if (SKIP_NESTED_KEYS.has(key) || key.startsWith('@')) {
        return
      }
      collectNestedStrings(nested, strings, depth + 1)
    })
  }
}

function getNestedFieldScore(row, term) {
  const strings = []
  collectNestedStrings(row, strings)

  let best = null
  strings.forEach((value) => {
    const score = scoreFieldValue(value, term)
    if (score !== null) {
      const adjusted = score + 10
      best = best === null ? adjusted : Math.min(best, adjusted)
    }
  })

  return best
}

export function getSearchRelevanceScore(row, searchTerm) {
  const term = normalizeForSearch(searchTerm)
  if (!term) {
    return 0
  }

  const primary = getPrimaryFieldScore(row, term)
  const nested = getNestedFieldScore(row, term)

  if (primary !== null && nested !== null) {
    return Math.min(primary, nested)
  }
  if (primary !== null) {
    return primary
  }
  if (nested !== null) {
    return nested
  }

  return 99
}

export function sortRowsBySearchRelevance(rows, searchTerm) {
  if (!rows?.length || !normalizeForSearch(searchTerm)) {
    return rows
  }

  return [...rows].sort((left, right) => {
    const scoreDiff =
      getSearchRelevanceScore(left, searchTerm) -
      getSearchRelevanceScore(right, searchTerm)

    if (scoreDiff !== 0) {
      return scoreDiff
    }

    return 0
  })
}
